import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import DefaultEditor from "react-simple-wysiwyg";
import {
  NoteService,
  Note,
  NoteComment,
  NoteAttachment,
} from "../../feature-module/crm/notes/note.service";
import {
  TenantUserService,
  TenantUser,
} from "../../feature-module/crm/leads/tenantUser.service";
import {
  showSuccessAlert,
  showErrorAlert,
  showDeleteConfirmationAlert,
} from "../../utils/alertUtils";
import { formatCustomDateTime } from "../../utils/dateUtils";
import { AuthContext } from "../../context/authcontext";
import male from "../../assets/male.png";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { Button } from "react-bootstrap";
import AttachmentComponent from "../AttachmentComponent";

const commentStyles = `
  .comment-item {
    transition: all 0.2s ease;
    border-left: 3px solid #e9ecef;
  }
  .comment-item:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-left-color: #0d6efd;
  }
  .comment-content p {
    margin-bottom: 0.5rem;
  }
  .comment-content p:last-child {
    margin-bottom: 0;
  }
  .comments-header {
    background: linear-gradient(45deg, #f8f9fa, #e9ecef);
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
  }
  .add-comment-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = commentStyles;
  document.head.appendChild(styleSheet);
}

const ascendingandDecending = [
  { value: "ascending", label: "Ascending" },
  { value: "descending", label: "Descending" },
];

interface NotesProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  related_entity_name?: string;
  refreshTrigger?: number;
  onActivityUpdate?: () => void;
}

const Notes: React.FC<NotesProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  refreshTrigger,
  onActivityUpdate,
}) => {
  const { auth } = useContext(AuthContext);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);

  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNote, setNewNote] = useState({
    title: "",
    description: "",
  });
  const [noteAttachments, setNoteAttachments] = useState<File[]>([]);

  const [noteComments, setNoteComments] = useState<{
    [noteId: number]: NoteComment[];
  }>({});
  const [loadingComments, setLoadingComments] = useState<{
    [noteId: number]: boolean;
  }>({});
  const [savingNote, setSavingNote] = useState(false);
  const [updatingNote, setUpdatingNote] = useState(false);
  const [addingComment, setAddingComment] = useState(false);

  const [showAddCommentModal, setShowAddCommentModal] = useState(false);
  const [selectedNoteForComment, setSelectedNoteForComment] = useState<
    number | null
  >(null);
  const [newComment, setNewComment] = useState({
    content: "",
  });

  const [noteAttachmentsList, setNoteAttachmentsList] = useState<{
    [noteId: number]: NoteAttachment[];
  }>({});

  const [collapsedComments, setCollapsedComments] = useState<{
    [noteId: number]: boolean;
  }>({});

  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [editNoteData, setEditNoteData] = useState({
    title: "",
    description: "",
  });
  const [editNoteAttachments, setEditNoteAttachments] = useState<File[]>([]);
  const [noteAttachmentsToDelete, setNoteAttachmentsToDelete] = useState<
    number[]
  >([]);
  const [downloadingAttachment, setDownloadingAttachment] = useState<
    number | null
  >(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const currentTenantId = Number(
    tenant_id || localStorage.getItem("selectedTenantId")
  );
  const currentRelatedEntityId = Number(related_entity_id);
  const currentUserId = auth?.user_id;

  const { isVisible, hasBeenActivated } = useTabVisibility({
    tabId: "notes",
    onTabActivated: () => {
      if (!dataLoaded && currentTenantId && currentRelatedEntityId) {
        loadNotes();
        loadTenantUsers();
        setDataLoaded(true);
      }
    },
  });

  useEffect(() => {
    if (
      hasBeenActivated &&
      !dataLoaded &&
      currentTenantId &&
      currentRelatedEntityId
    ) {
      loadNotes();
      loadTenantUsers();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, currentTenantId, currentRelatedEntityId]);

  useEffect(() => {
    if (
      dataLoaded &&
      refreshTrigger &&
      currentTenantId &&
      currentRelatedEntityId
    ) {
      loadNotes();
    }
  }, [refreshTrigger, dataLoaded, currentTenantId, currentRelatedEntityId]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const response = await NoteService.getNotes(
        currentTenantId,
        currentRelatedEntityId
      );
      setNotes(response.results || []);

      response.results?.forEach((note) => {
        if (note.note_id) {
          loadCommentsForNote(note.note_id);
          loadAttachmentsForNote(note.note_id);

          setCollapsedComments((prev) => ({
            ...prev,
            [note.note_id!]: true,
          }));
        }
      });
    } catch (error) {
      console.error("Error loading notes:", error);
      showErrorAlert("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  const loadTenantUsers = async () => {
    try {
      const users = await TenantUserService.getAllTenantUsers();
      setTenantUsers(users);
    } catch (error) {
      console.error("Error loading tenant users:", error);
    }
  };

  const loadCommentsForNote = async (noteId: number) => {
    try {
      setLoadingComments((prev) => ({ ...prev, [noteId]: true }));
      const comments = await NoteService.getNoteComments(noteId);
      setNoteComments((prev) => ({ ...prev, [noteId]: comments }));
    } catch (error) {
      console.error(`Error loading comments for note ${noteId}:`, error);
    } finally {
      setLoadingComments((prev) => ({ ...prev, [noteId]: false }));
    }
  };

  const loadAttachmentsForNote = async (noteId: number) => {
    try {
      const attachments = await NoteService.getNoteAttachments(
        currentTenantId,
        noteId
      );
      setNoteAttachmentsList((prev) => ({ ...prev, [noteId]: attachments }));
    } catch (error) {
      console.error(`Error loading attachments for note ${noteId}:`, error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.title.trim() || !newNote.description.trim()) {
      showErrorAlert("Please fill in both title and description");
      return;
    }

    if (!currentUserId) {
      showErrorAlert("User not authenticated");
      return;
    }

    try {
      setSavingNote(true);
      const noteData = {
        title: newNote.title,
        description: newNote.description,
        related_entity_id: currentRelatedEntityId,
        related_entity_name: related_entity_name,
        created_by: currentUserId,
        tenant_id: currentTenantId,
      };

      await NoteService.createNote(noteData, currentTenantId, noteAttachments);

      setNewNote({ title: "", description: "" });
      setNoteAttachments([]);
      setShowAddNoteModal(false);

      loadNotes();
      showSuccessAlert("Note added successfully");

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error) {
      console.error("Error adding note:", error);
      showErrorAlert("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      const result = await showDeleteConfirmationAlert(
        "Are you sure?",
        "This will permanently delete the note and all its comments.",
        "Yes, delete it!",
        "Cancel"
      );

      if (result.isConfirmed) {
        await NoteService.deleteNote(noteId, currentTenantId);
        loadNotes();
        showSuccessAlert("Note deleted successfully");

        if (onActivityUpdate) {
          onActivityUpdate();
        }
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      showErrorAlert("Failed to delete note");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.content?.trim()) {
      showErrorAlert("Please enter a comment");
      return;
    }

    if (!currentUserId || !selectedNoteForComment) {
      showErrorAlert("User not authenticated or no note selected");
      return;
    }

    try {
      setAddingComment(true);
      const commentData = {
        content: newComment.content,
        note_id: selectedNoteForComment,
        created_by: currentUserId,
        tenant_id: currentTenantId,
      };

      await NoteService.createNoteComment(commentData);

      setNewComment({ content: "" });
      setShowAddCommentModal(false);
      setSelectedNoteForComment(null);

      loadCommentsForNote(selectedNoteForComment);
      showSuccessAlert("Comment added successfully");

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      showErrorAlert("Failed to add comment");
    } finally {
      setAddingComment(false);
    }
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files) return;

    const allowedTypes = [
      ".jpg",
      ".jpeg",
      ".png",
      ".pdf",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
    ];
    const validFiles: File[] = [];

    Array.from(files).forEach((file) => {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (allowedTypes.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        showErrorAlert(
          `File "${file.name}" is not allowed. Only ${allowedTypes.join(
            ", "
          )} files are permitted.`
        );
      }
    });

    setNoteAttachments((prev) => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setNoteAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatDateTime = (dateString: string) => {
    return formatCustomDateTime(dateString);
  };

  const getUserName = (createdBy: any) => {
    if (typeof createdBy === "object" && createdBy?.full_name) {
      return createdBy.full_name;
    }
    return "Unknown User";
  };

  const getUserAvatar = (createdBy: any) => {
    if (typeof createdBy === "object" && createdBy?.avatar_url) {
      return createdBy.avatar_url;
    }
    return male;
  };

  const getAttachmentIcon = (filename: string | null | undefined) => {
    if (!filename) return "assets/img/icons/file.svg";
    const extension = filename.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "assets/img/icons/pdf.svg";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "assets/img/icons/image.svg";
      case "doc":
      case "docx":
        return "assets/img/icons/word.png";
      case "xls":
      case "xlsx":
        return "assets/img/icons/xls.svg";
      default:
        return "assets/img/icons/file.svg";
    }
  };

  const truncateFileName = (filename: string, maxLength: number = 20) => {
    if (!filename || filename.length <= maxLength) return filename;

    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${filename.substring(0, maxLength - 3)}...`;
    }

    const extension = filename.substring(lastDotIndex);
    const nameWithoutExt = filename.substring(0, lastDotIndex);

    if (extension.length >= maxLength - 3) {
      return `${filename.substring(0, maxLength - 3)}...`;
    }

    const availableLength = maxLength - extension.length - 3;

    if (nameWithoutExt.length <= availableLength) {
      return filename;
    }

    return `${nameWithoutExt.substring(0, availableLength)}...${extension}`;
  };

  const toggleCommentsCollapse = (noteId: number) => {
    setCollapsedComments((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));
  };

  const downloadAttachment = async (
    url: string,
    filename: string,
    attachmentId: number
  ) => {
    try {
      setDownloadingAttachment(attachmentId);
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);

      window.open(url, "_blank");
    } finally {
      setDownloadingAttachment(null);
    }
  };

  return (
    <>
      <style>
        {`
          .comment-item {
            transition: all 0.2s ease;
            border-left: 3px solid #e9ecef;
          }
          .comment-item:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border-left-color: #0d6efd;
          }
          .comment-content p {
            margin-bottom: 0.5rem;
          }
          .comment-content p:last-child {
            margin-bottom: 0;
          }
          .comments-header {
            background: linear-gradient(45deg, #f8f9fa, #e9ecef);
            border-radius: 8px;
            padding: 12px 16px;
            margin-bottom: 16px;
            transition: all 0.2s ease;
          }
          .comments-header:hover {
            background: linear-gradient(45deg, #e9ecef, #dee2e6);
          }
          .add-comment-btn {
            transition: all 0.2s ease;
          }
          .add-comment-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
          }
        `}
      </style>
      <div className="tab-pane fade" id="notes">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <h4 className="fw-semibold">Notes</h4>
            <div className="d-inline-flex align-items-center">
              <Button
                className="btn btn-primary"
                onClick={() => setShowAddNoteModal(true)}
              >
                <i className="ti ti-circle-plus me-1" />
                Add New Note
              </Button>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="notes-activity">
                {notes.length === 0 ? (
                  <div className="text-center p-4">
                    <p>
                      No notes found. Click "Add New" to create your first note.
                    </p>
                  </div>
                ) : (
                  notes.map((note) => (
                    <div key={note.note_id} className="card mb-3">
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between pb-2">
                          <div className="d-inline-flex align-items-center mb-2">
                            <span className="avatar avatar-md me-2 flex-shrink-0">
                              <img
                                src={getUserAvatar(note.created_by)}
                                alt="img"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "50%",
                                }}
                              />
                            </span>
                            <div>
                              <h6 className="fw-medium mb-1">
                                {getUserName(note.created_by)}
                              </h6>
                              <p className="mb-0">
                                {note.created_datetime
                                  ? formatDateTime(note.created_datetime)
                                  : "Just now"}
                              </p>
                            </div>
                          </div>
                          <div className="mb-2">
                            <div className="dropdown">
                              <Link
                                to="#"
                                className="p-0 btn btn-icon btn-sm d-flex align-items-center justify-content-center"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <i className="ti ti-dots-vertical" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <Link
                                  className="dropdown-item"
                                  to="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setEditingNote(note.note_id!);
                                    setEditNoteData({
                                      title: note.title,
                                      description: note.description,
                                    });
                                  }}
                                >
                                  <i className="ti ti-edit text-blue me-1" />
                                  Edit
                                </Link>
                                <Link
                                  className="dropdown-item"
                                  to="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteNote(note.note_id!);
                                  }}
                                >
                                  <i className="ti ti-trash text-danger me-1" />
                                  Delete
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>

                        {editingNote === note.note_id ? (
                          <div className="mb-3">
                            <input
                              type="text"
                              className="form-control mb-2"
                              value={editNoteData.title ?? ""}
                              onChange={(e) =>
                                setEditNoteData({
                                  ...editNoteData,
                                  title: e.target.value,
                                })
                              }
                              placeholder="Note title"
                            />
                            <DefaultEditor
                              value={editNoteData.description ?? ""}
                              onChange={(e) =>
                                setEditNoteData({
                                  ...editNoteData,
                                  description: e.target.value,
                                })
                              }
                            />

                            {/* Attachment Management */}
                            <div className="mt-3">
                              <label className="form-label">Attachments</label>

                              {/* Existing Attachments */}
                              {noteAttachmentsList[note.note_id!] &&
                                noteAttachmentsList[note.note_id!].length >
                                  0 && (
                                  <div className="mb-3">
                                    <h6 className="fw-medium mb-2">
                                      Current Attachments
                                    </h6>
                                    {noteAttachmentsList[note.note_id!].map(
                                      (attachment) => (
                                        <div
                                          key={attachment.note_attachment_id}
                                          className="upload-file upload-list"
                                        >
                                          <div className="d-flex align-items-center">
                                            <ImageWithBasePath
                                              src={getAttachmentIcon(
                                                attachment.attachment_url
                                              )}
                                              alt="file"
                                              className="me-2"
                                            />
                                            <div>
                                              <h6 title={attachment.title}>
                                                {truncateFileName(
                                                  attachment.title,
                                                  25
                                                )}
                                              </h6>
                                            </div>
                                          </div>
                                          <Link
                                            to="#"
                                            className="text-danger"
                                            onClick={() => {
                                              setNoteAttachmentsToDelete(
                                                (prev) => [
                                                  ...prev,
                                                  attachment.note_attachment_id!,
                                                ]
                                              );
                                              setNoteAttachmentsList(
                                                (prev) => ({
                                                  ...prev,
                                                  [note.note_id!]: prev[
                                                    note.note_id!
                                                  ].filter(
                                                    (att) =>
                                                      att.note_attachment_id !==
                                                      attachment.note_attachment_id
                                                  ),
                                                })
                                              );
                                            }}
                                          >
                                            <i className="ti ti-trash-x" />
                                          </Link>
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}

                              {/* Add New Attachments */}
                              <AttachmentComponent
                                attachments={editNoteAttachments}
                                allowUpload={true}
                                maxFiles={5}
                                maxFileSize={10 * 1024 * 1024}
                                acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"]}
                                onFileUpload={(files) => setEditNoteAttachments(prev => [...prev, ...files])}
                                onFileRemove={(index) => {
                                  setEditNoteAttachments((prev) =>
                                    prev.filter((_, i) => i !== index)
                                  );
                                }}
                                variant="compact"
                              />
                            </div>

                            <div className="d-flex gap-2 mt-2">
                              <button
                                className="btn btn-sm btn-primary"
                                disabled={updatingNote}
                                onClick={async () => {
                                  try {
                                    setUpdatingNote(true);

                                    for (const attachmentId of noteAttachmentsToDelete) {
                                      try {
                                        await NoteService.deleteNoteAttachment(
                                          attachmentId
                                        );
                                      } catch (error) {
                                        console.error(
                                          `Error deleting attachment ${attachmentId}:`,
                                          error
                                        );
                                      }
                                    }

                                    await NoteService.updateNote(
                                      note.note_id!,
                                      editNoteData,
                                      currentTenantId,
                                      editNoteAttachments.length > 0
                                        ? editNoteAttachments
                                        : undefined
                                    );

                                    setEditingNote(null);
                                    setEditNoteAttachments([]);
                                    setNoteAttachmentsToDelete([]);
                                    loadNotes();
                                    showSuccessAlert(
                                      "Note updated successfully"
                                    );

                                    if (onActivityUpdate) {
                                      onActivityUpdate();
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error updating note:",
                                      error
                                    );
                                    showErrorAlert("Failed to update note");
                                  } finally {
                                    setUpdatingNote(false);
                                  }
                                }}
                              >
                                {updatingNote ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" />
                                    Updating...
                                  </>
                                ) : (
                                  "Save"
                                )}
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setEditingNote(null);
                                  setEditNoteAttachments([]);
                                  setNoteAttachmentsToDelete([]);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h5 className="fw-medium mb-1">{note.title}</h5>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: note.description,
                              }}
                            />

                            {/* Note Attachments */}
                            {noteAttachmentsList[note.note_id!] &&
                              noteAttachmentsList[note.note_id!].length > 0 && (
                                <div className="file-attach mt-3">
                                  <h6 className="fw-medium mb-2">
                                    Attachments
                                  </h6>
                                  <div className="row">
                                    {noteAttachmentsList[note.note_id!].map(
                                      (attachment) => (
                                          <div
                                            key={attachment.note_attachment_id}
                                            className="col-md-4 mb-2"
                                        >
                                          <div className="attach-file-list d-flex align-items-center border rounded p-2">
                                            <div className="attach-img me-2">
                                              <ImageWithBasePath
                                                src={
                                                  getAttachmentIcon(
                                                    attachment.attachment_url
                                                  ) || male
                                                }
                                                alt="icon"
                                              />
                                            </div>
                                            <div className="attach-content flex-fill">
                                              <h6
                                                className="mb-1"
                                                title={attachment.title}
                                              >
                                                {truncateFileName(
                                                  attachment.title
                                                )}
                                              </h6>
                                              <p className="mb-0 text-muted small">
                                                {attachment.size_in_kb} KB
                                              </p>
                                            </div>
                                            <div className="attach-action">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                disabled={
                                                  downloadingAttachment ===
                                                  attachment.note_attachment_id
                                                }
                                                onClick={() =>
                                                  downloadAttachment(
                                                    attachment.attachment_url,
                                                    attachment.title,
                                                    attachment.note_attachment_id!
                                                  )
                                                }
                                              >
                                                {downloadingAttachment ===
                                                attachment.note_attachment_id ? (
                                                  <span className="spinner-border spinner-border-sm" />
                                                ) : (
                                                  <i className="ti ti-arrow-down" />
                                                )}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </>
                        )}

                        {/* Comments Section */}
                        <div className="notes-editor mt-4">
                          {/* Existing Comments */}
                          {noteComments[note.note_id!] &&
                            noteComments[note.note_id!].length > 0 && (
                              <div className="comments-section mb-3">
                                <div
                                  className="comments-header d-flex align-items-center justify-content-between"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    toggleCommentsCollapse(note.note_id!)
                                  }
                                >
                                  <h6 className="fw-medium mb-0 d-flex align-items-center">
                                    <i className="ti ti-message-circle me-2 text-primary"></i>
                                    Comments (
                                    {noteComments[note.note_id!].length})
                                  </h6>
                                  <i
                                    className={`ti ${
                                      collapsedComments[note.note_id!]
                                        ? "ti-chevron-down"
                                        : "ti-chevron-up"
                                    } text-muted`}
                                  ></i>
                                </div>
                                {!collapsedComments[note.note_id!] && (
                                  <div className="comments-list mt-2">
                                    {noteComments[note.note_id!].map(
                                      (comment, index) => (
                                        <div
                                          key={comment.note_comment_id}
                                          className="comment-item border rounded p-3 mb-2 bg-light position-relative"
                                        >
                                          <div className="d-flex align-items-start">
                                            <span className="avatar avatar-sm me-2 flex-shrink-0">
                                              <img
                                                src={
                                                  comment.created_by
                                                    ?.avatar_url || male
                                                }
                                                alt="User Avatar"
                                                style={{
                                                  width: "40px",
                                                  height: "40px",
                                                  borderRadius: "50%",
                                                }}
                                              />
                                            </span>
                                            <div className="flex-fill">
                                              <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="d-flex align-items-center">
                                                  <h6 className="fw-medium mb-0 me-2">
                                                    {comment.created_by
                                                      ?.full_name ||
                                                      "Unknown User"}
                                                  </h6>
                                                  <span className="badge bg-light text-dark small">
                                                    #{index + 1}
                                                  </span>
                                                </div>
                                                <span className="text-muted small">
                                                  {comment.created_datetime
                                                    ? formatDateTime(
                                                        comment.created_datetime
                                                      )
                                                    : "Recently"}
                                                </span>
                                              </div>
                                              <div
                                                className="comment-content"
                                                dangerouslySetInnerHTML={{
                                                  __html: comment.content,
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Add Comment Button */}
                          <div className="text-center mt-3">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm add-comment-btn"
                              onClick={() => {
                                setSelectedNoteForComment(note.note_id!);
                                setShowAddCommentModal(true);
                              }}
                            >
                              <i className="ti ti-message-circle-plus me-1" />
                              Add Comment
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      {showAddNoteModal && (
        <div
          className="modal fade show d-block"
          id="add_notes"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Add New Note</h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNewNote({ title: "", description: "" });
                    setNoteAttachments([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newNote.title ?? ""}
                    onChange={(e) =>
                      setNewNote({ ...newNote, title: e.target.value })
                    }
                    placeholder="Enter note title"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Description <span className="text-danger">*</span>
                  </label>
                  <DefaultEditor
                    value={newNote.description ?? ""}
                    onChange={(e) =>
                      setNewNote({ ...newNote, description: e.target.value })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Attachments</label>
                  <AttachmentComponent
                    attachments={noteAttachments}
                    allowUpload={true}
                    maxFiles={5}
                    maxFileSize={10 * 1024 * 1024}
                    acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"]}
                    onFileUpload={(files) => setNoteAttachments(prev => [...prev, ...files])}
                    onFileRemove={(index) => removeAttachment(index)}
                    variant="compact"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNewNote({ title: "", description: "" });
                    setNoteAttachments([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingNote}
                  onClick={handleAddNote}
                >
                  {savingNote ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Adding...
                    </>
                  ) : (
                    "Add Note"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Comment Modal */}
      {showAddCommentModal && (
        <div
          className="modal fade show d-block"
          id="add_comment"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Add Comment</h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddCommentModal(false);
                    setSelectedNoteForComment(null);
                    setNewComment({ content: "" });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Comment <span className="text-danger">*</span>
                  </label>
                  <DefaultEditor
                    value={newComment.content ?? ""}
                    onChange={(e) =>
                      setNewComment({ ...newComment, content: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setShowAddCommentModal(false);
                    setSelectedNoteForComment(null);
                    setNewComment({ content: "" });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={addingComment}
                  onClick={handleAddComment}
                >
                  {addingComment ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-send me-1" />
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notes;
